import { layoutWithDraftBasis } from '@repo/front-end-state';
import type { FloorPlannerLayout, TwinLaunchSummary } from '@repo/frontend-types';

/**
 * What loads when the wizard opens a saved plan (spec `twin-launch-history-draft-choice`, R1.5 and
 * R2.2) — pure, so the single definition of "this plan holds unlaunched work" and of "what loads"
 * is testable on its own, and Versions can reuse it.
 *
 * Rulings behind it:
 * - **An edited draft always opens as saved, with its own observers** (Rob, 2026-10-27, spec
 *   `remove-draft-choice-modal`). The opening *keep the draft / use production* question is gone;
 *   Versions' *Discard Draft* is the way back to production.
 * - **An UNEDITED plan takes the running observers** (maria@twinlabs.ai, 2026-10-05): nothing is
 *   lost by it, and an old plan with no observers would otherwise open empty beside the live ones.
 * - **Nothing is replaced on a failed read.** "Could not find out" is never "no launches".
 */

/** The production read, as the page holds it. */
export type ProductionRead =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; launch: TwinLaunchSummary | null; inProgress: boolean };

/** Which observers the first live-configuration match keeps (`useLiveTwinConfiguration`). */
export type FirstMatchSource = 'draft' | 'running';

/** Why the draft is shown as saved, which the Twin Details step says. */
export type DraftChoiceNotice = 'production-unread' | 'launch-in-progress';

/** Load the plan as saved, with this first-match source, and perhaps a notice. */
export type DraftDecision = {
  kind: 'load';
  firstMatchSource: FirstMatchSource;
  notice: DraftChoiceNotice | null;
};

/**
 * True when `a` is not an earlier instant than `b`. Unparseable is never.
 *
 * ⚠ Equal counts: `Date.parse` keeps milliseconds, and the database stamps both times to the
 * microsecond, so a plan saved just after a launch in the same millisecond compares equal. When
 * the order cannot be told, the plan is taken as edited — keeping its own observers is safe,
 * replacing them with the running ones is not (PR review, 2026-10-06).
 */
const notEarlier = (a: string, b: string): boolean => {
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  return Number.isFinite(ta) && Number.isFinite(tb) && ta >= tb;
};

/**
 * Whether the plan holds work saved but not launched (R1.5) — the single definition.
 *
 * 1. a legacy `observerDraft` present → yes, WHATEVER `draftBasis` says. It is only ever written
 *    by an edit (by a twin-viewer from before this feature, or as this one's compatibility copy),
 *    and every launch reset removes it. A tab still running the old bundle edits a plan whose
 *    `draftBasis` a launch reset to `editedAt: null` and adds only this marker — so reading
 *    `draftBasis` first would call that edited plan launched.
 * 2. else `draftBasis` present → its `editedAt` is set.
 * 3. else neither (a plan saved between the launch record shipping and this feature, or before
 *    either): yes only if the plan was saved after the latest completed launch.
 */
export const hasUnlaunchedEdits = (
  layout: FloorPlannerLayout,
  planModifiedAt: string | null,
  latestLaunch: Pick<TwinLaunchSummary, 'completedAt'> | null,
): boolean => {
  if (layout.observerDraft) return true;
  if (layout.draftBasis) return layout.draftBasis.editedAt !== null;
  return (
    !!latestLaunch?.completedAt &&
    planModifiedAt !== null &&
    notEarlier(planModifiedAt, latestLaunch.completedAt)
  );
};

/**
 * What the wizard does on open with a loaded plan (R2.2). The caller waits while production is
 * `loading`; a plan that is absent, failed or unread never reaches here.
 */
export const decideDraftChoice = (args: {
  layout: FloorPlannerLayout;
  planModifiedAt: string | null;
  production: Exclude<ProductionRead, { status: 'loading' }>;
}): DraftDecision => {
  const { layout, planModifiedAt, production } = args;
  if (production.status === 'failed') {
    // We could not find out, so nothing is replaced — edited or not — and the person is told.
    return { kind: 'load', firstMatchSource: 'draft', notice: 'production-unread' };
  }
  const edited = hasUnlaunchedEdits(layout, planModifiedAt, production.launch);
  if (!edited) return { kind: 'load', firstMatchSource: 'running', notice: null };
  if (production.inProgress) {
    return { kind: 'load', firstMatchSource: 'draft', notice: 'launch-in-progress' };
  }
  // Launched or not, an edited draft opens as saved (Rob, 2026-10-27).
  return { kind: 'load', firstMatchSource: 'draft', notice: null };
};

/**
 * Whether production has been launched again since this draft was started — Versions' warning.
 * A legacy marker cannot say, so it counts; a basis that could not be read (null) does not, a
 * deliberate under-claim.
 */
export const startedBeforeProduction = (
  layout: FloorPlannerLayout,
  launch: Pick<TwinLaunchSummary, 'launchUuid'>,
): boolean => {
  if (layout.draftBasis) {
    return (
      layout.draftBasis.basedOnLaunch !== null &&
      layout.draftBasis.basedOnLaunch !== launch.launchUuid
    );
  }
  return !!layout.observerDraft;
};

/** Drop the retired observers-only marker from a layout. */
const withoutLegacyMarker = (layout: FloorPlannerLayout): FloorPlannerLayout => {
  const { observerDraft: _retired, ...rest } = layout;
  return rest;
};

/**
 * The marker a first edit writes (R1.2), or `null` when the plan already carries one — the first
 * edit's basis is the one that matters. A legacy `observerDraft` keeps its own `editedAt`.
 *
 * `editedBy` is who is making this edit (`twin-details-page-version-changes`, R5) — except over a
 * legacy `observerDraft`, whose draft was started earlier by someone this plan never recorded, so
 * it is `null` there rather than credited to whoever happened to edit next.
 */
export const firstEditBasis = (
  layout: FloorPlannerLayout,
  basedOnLaunch: string | null,
  nowIso: string,
  editedBy?: string | null,
): NonNullable<FloorPlannerLayout['draftBasis']> | null => {
  if (layout.draftBasis?.editedAt) return null;
  return {
    editedAt: layout.observerDraft?.editedAt ?? nowIso,
    // The launch the page knows as production now; else the one the plan was last reset to.
    basedOnLaunch: basedOnLaunch ?? layout.draftBasis?.basedOnLaunch ?? null,
    editedBy: layout.observerDraft ? null : (editedBy ?? null),
  };
};

/**
 * `layout` marked as holding no unlaunched work, based on `launchUuid` — what Go live writes after
 * its filing (R1.4), and what *Discard Draft* writes on production's plan (`asLoadedLaunch`).
 */
export const withLaunchBasis = (
  layout: FloorPlannerLayout,
  launchUuid: string | null,
): FloorPlannerLayout => {
  /*
   * A launch makes the running twin every observer's reference again, so the drawn-against records
   * go with the draft marker (`onboarding-polish-observer-notices`, R15).
   */
  /*
   * The same goes for what the conversion skipped when the plan was started from the live twin
   * (`floor-plan-page-start`, R12): once launched, those entities' absence is what runs.
   */
  /*
   * And the renumbering record (`floor-plan-page-launch-bid-changes`, R15): written only after a
   * filing succeeds, so the renumberings it carried are now what runs. `retiredEntityCodes` is kept —
   * it is about the twin, and the next draft must still never mint one of them.
   */
  const {
    observerReferenceGeometry: _launched,
    adoptionSkipped: _settled,
    entityCodeChanges: _carried,
    ...rest
  } = withoutLegacyMarker(layout);
  return { ...rest, draftBasis: { editedAt: null, basedOnLaunch: launchUuid } };
};

/**
 * `plan` with the current plan's retired BIDs folded in (`floor-plan-page-launch-bid-changes`, R10).
 * A recorded launch's plan carries the list as it stood THEN, so loading it would otherwise forget
 * every BID retired since — and a later mint could issue one again.
 */
const withRetiredFrom = (
  plan: FloorPlannerLayout,
  current: readonly string[] | null | undefined,
): FloorPlannerLayout => {
  const merged = Array.from(
    new Set([...(plan.retiredEntityCodes ?? []), ...(current ?? [])].filter(Boolean)),
  ).sort();
  return merged.length > 0 ? { ...plan, retiredEntityCodes: merged } : plan;
};

/**
 * A recorded launch's plan as the history panel's *Load as draft* writes it
 * (`twin-launch-history-panel`, R2) — the single definition.
 *
 * - **Production** (`launchUuid === productionUuid`): exactly what *Discard Draft* writes — no
 *   unlaunched work, based on production — so its observers are then matched to what is running.
 * - **An earlier launch**: marked as unlaunched work NOW, based on production, naming the launch it
 *   came from (`loadedFromLaunch`), with the one-release compatibility copy. Marked, because an
 *   unmarked plan resumes with the RUNNING observers on the next open (R2.2 of draft-choice), which
 *   would silently put production's observers over the launch that was loaded; and based on
 *   production, so Versions does not claim production was launched again since.
 *   Loading it is how that draft started, so it records who loaded it (`editedBy`;
 *   `twin-details-page-version-changes`, R5.4). Production records nobody: it is no draft.
 */
export const asLoadedLaunch = (
  plan: FloorPlannerLayout,
  args: {
    launchUuid: string;
    productionUuid: string;
    nowIso: string;
    editedBy?: string | null;
    /** The CURRENT plan's retired BIDs, kept across the swap (R10). */
    retiredEntityCodes?: readonly string[] | null;
  },
): FloorPlannerLayout => {
  plan = withRetiredFrom(plan, args.retiredEntityCodes);
  if (args.launchUuid === args.productionUuid) return withLaunchBasis(plan, args.productionUuid);
  // A recorded plan's drawn-against records describe the twin as it was then; like Go live and
  // *Discard Draft*, loading a launch drops them, so the running twin is the reference again
  // (`onboarding-polish-observer-notices`, R15). Its skipped-entities note goes too, as
  // `withLaunchBasis` drops it: that launch settled those entities (`floor-plan-page-start`, R12).
  // And its renumbering record (`floor-plan-page-launch-bid-changes`, review 2026-10-17): a recorded
  // plan is the plan AS FILED, captured before the record was cleared, so it still names renumberings
  // that launch already carried. Kept, it would replay them against a twin that has moved on.
  const {
    observerReferenceGeometry: _then,
    adoptionSkipped: _settled,
    entityCodeChanges: _carried,
    ...rest
  } = plan;
  return layoutWithDraftBasis(rest, {
    editedAt: args.nowIso,
    basedOnLaunch: args.productionUuid,
    loadedFromLaunch: args.launchUuid,
    editedBy: args.editedBy ?? null,
  });
};
